import { usePrevious } from '@engoo/react-hooks/usePrevious';
import { createLessonOutcomeMessaging } from 'app/components/call/BookingDetails/LessonBookingDetails/LessonOutcome/LessonOutcomeMessaging';
import ModalHelper from 'app/components/call/BookingDetails/LessonBookingDetails/LessonOutcome/Modal/ModalHelper';
import TimedOutModal from 'app/components/call/BookingDetails/LessonBookingDetails/LessonOutcome/Modal/TimedOut';
import { useI18n } from 'app/components/localization';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { CallOutcomeType } from 'app/containers/call/CallOutcome';
import { CallStage, ICallStateContext, useCallStateContext } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType, ILessonBookingState } from 'app/models/rtc/booking/constants';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

// Map of phases that can be used to detect if the user is or was effectively on a lesson booking call.
// This is designed to be exhaustive, so if new CallStage are added there will be a type error and an
// audit will be required.
const LessonOutcomeModalCallStageMap: {
  [T in CallStage]: boolean;
} = {
  [CallStage.Unknown]: false,
  [CallStage.AwaitingActivation]: true,
  [CallStage.Active]: true,
  [CallStage.PostLocked]: false,
};

interface ILessonOutcomeModalProps {
  callStateContext: ICallStateContext;
  lessonBookingState: ILessonBookingState;
}

/**
 * Handles rendering a modal in the event a lesson call ended.
 */
const LessonOutcomeModal: FC<ILessonOutcomeModalProps> = ({ callStateContext, lessonBookingState }) => {
  const { t } = useI18n();

  // Maintain previous states that can be used to determine if a booking outcome was reached.
  const previousBookingState = usePrevious(lessonBookingState);
  const previousCallStateContext = usePrevious(callStateContext);

  // Describes the potential of showing a modal. As some of the information
  // may not readily be available immedietely at the end of the lesson.
  const [showModal, setShowModal] = useState(false);

  // For the specification, the modal should only show once if the user is on the call or awaiting the call.
  // This can be detected by making either of these checks:
  //
  // 1. The LessonBookingOutcome goes from null to not null. This handles cases where the user is on a booking, but may
  // not be on a call.
  //
  // 2. The CallStage goes from AwaitingActivation or Active to PostLocked. This handles the case that the user
  // is on the call and the outcome may be derived from CallState.
  const bookingChanged = previousBookingState?.bookingId !== lessonBookingState.bookingId;
  const bookingOutcomeChanged =
    !bookingChanged &&
    previousBookingState?.outcome === null && lessonBookingState.outcome !== null;

  // Test for case #2, that the CallStage has changed to PostLocked.
  const callStagePostLocked =
    LessonOutcomeModalCallStageMap[previousCallStateContext?.stage ?? CallStage.Unknown] &&
    callStateContext.stage === CallStage.PostLocked;

  // If either of these conditions turns out to be true then turn the modal on.
  const enableModal = bookingOutcomeChanged || callStagePostLocked;
  useEffect(() => {
    if (enableModal) {
      setShowModal(true);
    }
  }, [enableModal]);

  // If the booking changed, then disable the modal.
  useEffect(() => {
    if (bookingChanged) {
      setShowModal(false);
    }
  }, [bookingChanged]);

  const onClose = useCallback(() => {
    setShowModal(false);
  }, []);

  // Derive the lesson outcome messaging if there are adequate conditions for showing them.
  const lessonOutcomeMessaging = useMemo(() => {
    // An outcome cannot be derived from either of these cases.
    if (lessonBookingState.lessonRole === LessonBookingRole.Other || callStateContext.outcome === null) {
      return null;
    }

    return createLessonOutcomeMessaging(t, lessonBookingState.lessonRole, callStateContext.outcome);
  }, [callStateContext.outcome, lessonBookingState.lessonRole, t]);

  if (showModal) {
    // Conditions in which to show the TimedOut modal, which requires special handling due
    // to the styling concerns of the content.
    if (
      callStateContext.outcome?.type === CallOutcomeType.TimedOut &&
      lessonBookingState.lessonRole === LessonBookingRole.Student
    ) {
      return <TimedOutModal onClose={onClose} />;
    }

    // There is a modal to show the user based on the booking outcome.
    if (lessonOutcomeMessaging !== null && lessonOutcomeMessaging.modal !== null) {
      return <ModalHelper
        alert={lessonOutcomeMessaging.alert}
        asset={lessonOutcomeMessaging.asset}
        buttons={lessonOutcomeMessaging.buttons}
        onClose={onClose}
        status={lessonOutcomeMessaging.status.summary}
        title={lessonOutcomeMessaging.modal.title}
      />;
    }
  }

  return null;
};

/**
 * Helper to eliminate testing for edge cases.
 */
const LessonOutcomeModalInjectDependencies = () => {
  const callStateContext = useCallStateContext();
  const bookingState = useBookingState();

  // Don't render if the booking type is not a lesson.
  if (bookingState?.bookingType !== BookingType.Lesson) {
    return null;
  }

  return <LessonOutcomeModal callStateContext={callStateContext} lessonBookingState={bookingState} />;
};

export default LessonOutcomeModalInjectDependencies;
